import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BoldText, Text } from '../../components/common/Text';
import { FadeInTransition } from '../../components/transitions/transitions';
import { PrimaryButton } from '../../components/common/Buttons';
import { submitSsoMissingFields } from '../../actions/index';
import {
  NAME_MAX_LENGTH,
  fieldSchema,
  isFieldValid,
  maxDobForAdult,
  validationError,
} from '../../utils/ssoFieldSchema';
import checkedIcon from '../../images/onboarding/checked.svg';
import warningIcon from '../../images/onboarding/exclaimation.svg';
import onboardingGraphic from '../../images/onboarding/onboarding-graphic-right.svg';

const SUCCESS_BORDER = 'border-[#36B37E]';
const ERROR_BORDER = 'border-[#FF5630]';
const ERROR_TEXT = 'text-[#FF5630]';

function inputShellClass(schema, value, focused) {
  if (focused) return 'border-primary';
  if (!value) return 'border-txtlight-shades2';
  return isFieldValid(schema, value) ? SUCCESS_BORDER : ERROR_BORDER;
}

export default function CompleteProfileSso({ history, location }) {
  const dispatch = useDispatch();
  const requiredFields = useSelector((state) => state.app.ssoRequiredFields);
  const storedClientId = useSelector((state) => state.app.ssoClientId);
  const storedEncrypted = useSelector((state) => state.app.ssoEncrypted);
  // Route state wins: the `app` Redux slice is reset by setInitialState().
  const encrypted = location?.state?.encrypted || storedEncrypted;
  const clientId = location?.state?.clientId || storedClientId;
  const hasError = useSelector((state) => state.app.appError?.hasError);
  const [values, setValues] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!requiredFields || !requiredFields.length) {
      history.replace('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Submission failed (saga dispatched SHOW_ERROR) — release the button so
  // the user can correct input and retry instead of watching it spin.
  useEffect(() => {
    if (hasError) {
      setLoading(false);
    }
  }, [hasError]);

  if (!requiredFields || !requiredFields.length) {
    return null;
  }

  const allFieldsFilled = requiredFields.every((fieldName) =>
    isFieldValid(fieldSchema(fieldName), values[fieldName])
  );

  const handleFieldChange = (fieldName, schema, rawValue) => {
    let value = rawValue;
    if (schema.type === 'tel') {
      value = rawValue.replace(/\D/g, '').slice(0, 10);
    } else if (schema.type === 'text') {
      value = rawValue.slice(0, NAME_MAX_LENGTH);
    }
    setValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = () => {
    setLoading(true);
    dispatch(submitSsoMissingFields(values, clientId, encrypted, history));
  };

  return (
    <FadeInTransition in={true} timeout={200} mountOnEnter unmountOnExit appear>
      <div className="flex flex-1 flex-col bg-primary-light overflow-y-auto">
        <div className="flex flex-1 flex-col mx-4 my-6 rounded-20 bg-white shadow-card px-6 py-8">
          <div className="relative flex justify-end -mt-2 -mr-2 mb-2">
            <img
              src={onboardingGraphic}
              alt=""
              className="w-14 h-14 opacity-90"
            />
          </div>

          <div className="text-caption-l font-bold uppercase tracking-wide text-primary mb-2">
            One quick step
          </div>
          <BoldText className="text-title-l leading-tight mb-2">
            Let&rsquo;s finish setting up your account
          </BoldText>
          <Text className="text-body-l text-txtlight-shades1 mb-8 max-w-[34ch]">
            We just need a couple more details
          </Text>

          {requiredFields.map((fieldName) => {
            const schema = fieldSchema(fieldName);
            const value = values[fieldName] || '';
            const valid = isFieldValid(schema, value);
            const errorMessage = validationError(schema, value);
            return (
              <div key={fieldName} className="mb-25">
                <div className="text-body-l font-semibold text-txtlight mb-2">
                  {schema.label}
                </div>
                <div
                  className={`border ${inputShellClass(
                    schema,
                    value,
                    focusedField === fieldName
                  )} rounded-12 px-4 py-1 flex items-center justify-between bg-white transition-shadow`}
                >
                  {schema.type === 'select' ? (
                    <select
                      className="visit-input text-body-l font-semibold w-full py-3 bg-white"
                      value={value}
                      onFocus={() => setFocusedField(fieldName)}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e) =>
                        handleFieldChange(fieldName, schema, e.target.value)
                      }
                    >
                      <option value="">{schema.placeholder}</option>
                      {schema.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={schema.type}
                      inputMode={schema.type === 'tel' ? 'numeric' : undefined}
                      maxLength={
                        schema.type === 'text' ? NAME_MAX_LENGTH : undefined
                      }
                      max={
                        schema.type === 'date' ? maxDobForAdult() : undefined
                      }
                      className="visit-input text-body-l font-semibold w-full py-3"
                      placeholder={schema.placeholder}
                      value={value}
                      onFocus={() => setFocusedField(fieldName)}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e) =>
                        handleFieldChange(fieldName, schema, e.target.value)
                      }
                    />
                  )}
                  {valid && (
                    <img
                      src={checkedIcon}
                      alt="valid"
                      className="w-5 h-5 flex-shrink-0"
                    />
                  )}
                </div>
                {errorMessage && (
                  <div
                    className={`flex items-center ${ERROR_TEXT} text-caption-l font-medium mt-2`}
                  >
                    <img
                      src={warningIcon}
                      alt="warning"
                      className="mr-2 w-3.5 h-3.5"
                    />
                    {errorMessage}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex-1 min-h-6" />

          <PrimaryButton
            className="w-full mt-4"
            onTap={handleSubmit}
            loading={loading}
            disabled={!allFieldsFilled}
          >
            <div className="text-button-l font-bold text-center py-2">
              Continue
            </div>
          </PrimaryButton>
        </div>
      </div>
    </FadeInTransition>
  );
}
